/**
 * Public Offer Portal Page
 * 
 * Self-service portal for customers to view and sign offers
 * WITHOUT needing the signature email link.
 * 
 * Flow:
 * 1. Customer enters: Angebotsnummer, Nachname, PLZ
 * 2. System verifies the data
 * 3. Customer views the full offer PDF
 * 4. Customer clicks "Jetzt signieren" -> OTP is sent
 * 5. Redirects to existing signing flow
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { 
  FileText, 
  CheckCircle, 
  Shield, 
  Loader2,
  Eye,
  Pen,
  AlertCircle,
  Building2,
  User,
  MapPin,
  FileSignature,
  ArrowRight,
  RefreshCw
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function OfferPortalPage() {
  const navigate = useNavigate();
  
  // Verification State
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [sessionToken, setSessionToken] = useState(null);
  
  // Form State
  const [offerNumber, setOfferNumber] = useState("");
  const [lastName, setLastName] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [error, setError] = useState(null);
  
  // Offer Data State
  const [offerData, setOfferData] = useState(null);
  const [loadingOffer, setLoadingOffer] = useState(false);
  const [requestingSignature, setRequestingSignature] = useState(false);
  
  // PDF Viewer State
  const [showPdfViewer, setShowPdfViewer] = useState(false);

  // Handle verification
  const handleVerify = async (e) => {
    e.preventDefault();
    setError(null);
    setVerifying(true);
    
    try {
      const response = await fetch(`${API_URL}/api/public/portal/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          offer_number: offerNumber.trim(),
          last_name: lastName.trim(),
          zip_code: zipCode.trim()
        })
      });
      
      // Read the response body once
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || data.message || "Verifizierung fehlgeschlagen");
      }
      
      // Success!
      setSessionToken(data.session_token);
      setVerified(true);
      toast.success("Verifizierung erfolgreich!");
      
      // Load offer data
      await loadOfferData(data.session_token);
      
    } catch (err) {
      const errorMessage = err.message || "Ein Fehler ist aufgetreten";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setVerifying(false);
    }
  };
  
  // Load offer data after verification
  const loadOfferData = async (token) => {
    setLoadingOffer(true);
    
    try {
      const response = await fetch(
        `${API_URL}/api/public/portal/offer?session_token=${encodeURIComponent(token)}`
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 401) {
          // Session expired
          setVerified(false);
          setSessionToken(null);
          throw new Error("Sitzung abgelaufen. Bitte verifizieren Sie sich erneut.");
        }
        throw new Error(data.detail || "Fehler beim Laden des Angebots");
      }
      
      setOfferData(data);
      
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoadingOffer(false);
    }
  };
  
  // Request signature (triggers OTP)
  const handleRequestSignature = async () => {
    if (!sessionToken) return;
    
    setRequestingSignature(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${API_URL}/api/public/portal/offer/request-signature?session_token=${encodeURIComponent(sessionToken)}`,
        { method: "POST" }
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 401) {
          setVerified(false);
          setSessionToken(null);
          throw new Error("Sitzung abgelaufen. Bitte verifizieren Sie sich erneut.");
        }
        throw new Error(data.detail || "Fehler beim Anfordern der Signatur");
      }
      
      toast.success(`Bestätigungscode wurde an ${data.otp_sent_to} gesendet`);
      
      // Redirect to signing page - extract path from signing_url
      // The signing_url may contain a different base URL (production vs preview)
      try {
        const signingUrl = new URL(data.signing_url);
        const signingPath = signingUrl.pathname + signingUrl.search;
        navigate(signingPath);
      } catch {
        // Fallback: try to extract path manually
        const pathMatch = data.signing_url.match(/\/sign\/[^?]+\?token=[^&]+/);
        if (pathMatch) {
          navigate(pathMatch[0]);
        } else {
          // Last resort: use the full URL
          window.location.href = data.signing_url;
        }
      }
      
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setRequestingSignature(false);
    }
  };
  
  // Reset to verification form
  const handleReset = () => {
    setVerified(false);
    setSessionToken(null);
    setOfferData(null);
    setError(null);
    setShowPdfViewer(false);
  };
  
  // Format status for display
  const getStatusBadge = (status) => {
    const statusConfig = {
      "erstellt": { label: "Erstellt", variant: "secondary" },
      "versendet": { label: "Versendet", variant: "default" },
      "angenommen": { label: "Angenommen", variant: "default", className: "bg-green-500" },
      "abgelehnt": { label: "Abgelehnt", variant: "destructive" },
      "storniert": { label: "Storniert", variant: "destructive" }
    };
    
    const config = statusConfig[status] || { label: status, variant: "secondary" };
    
    return (
      <Badge variant={config.variant} className={config.className}>
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <FileSignature className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-slate-900">Angebot abrufen</h1>
              <p className="text-sm text-slate-500">
                Self-Service Portal zur Ansicht und Unterschrift
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {!verified ? (
          /* ========== VERIFICATION FORM ========== */
          <div className="max-w-md mx-auto">
            <Card className="shadow-lg border-0">
              <CardHeader className="text-center pb-2">
                <div className="mx-auto mb-4 p-3 bg-emerald-50 rounded-full w-fit">
                  <Shield className="h-8 w-8 text-emerald-600" />
                </div>
                <CardTitle className="text-2xl">Angebot verifizieren</CardTitle>
                <CardDescription className="text-base mt-2">
                  Geben Sie Ihre Daten ein, um Ihr Angebot anzusehen
                </CardDescription>
              </CardHeader>
              
              <CardContent className="pt-4">
                <form onSubmit={handleVerify} className="space-y-5">
                  {/* Offer Number */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      <FileText className="h-4 w-4 text-slate-400" />
                      Angebotsnummer
                    </label>
                    <Input
                      data-testid="portal-offer-number"
                      type="text"
                      placeholder="z.B. A-2025-0001"
                      value={offerNumber}
                      onChange={(e) => setOfferNumber(e.target.value)}
                      required
                      className="h-12 text-base"
                      autoComplete="off"
                    />
                  </div>
                  
                  {/* Last Name */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      <User className="h-4 w-4 text-slate-400" />
                      Nachname
                    </label>
                    <Input
                      data-testid="portal-last-name"
                      type="text"
                      placeholder="Ihr Nachname"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      className="h-12 text-base"
                      autoComplete="family-name"
                    />
                  </div>
                  
                  {/* ZIP Code */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-slate-400" />
                      Postleitzahl (PLZ)
                    </label>
                    <Input
                      data-testid="portal-zip-code"
                      type="text"
                      placeholder="z.B. 12345"
                      value={zipCode}
                      onChange={(e) => setZipCode(e.target.value.replace(/\D/g, "").slice(0, 5))}
                      required
                      maxLength={5}
                      pattern="[0-9]{5}"
                      className="h-12 text-base"
                      autoComplete="postal-code"
                    />
                  </div>
                  
                  {/* Error Message */}
                  {error && (
                    <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                      <span>{error}</span>
                    </div>
                  )}
                  
                  {/* Submit Button */}
                  <Button
                    data-testid="portal-verify-btn"
                    type="submit"
                    disabled={verifying || !offerNumber || !lastName || zipCode.length !== 5}
                    className="w-full h-12 text-base bg-emerald-600 hover:bg-emerald-700"
                  >
                    {verifying ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Verifiziere...
                      </>
                    ) : (
                      <>
                        <Shield className="h-5 w-5 mr-2" />
                        Angebot abrufen
                      </>
                    )}
                  </Button>
                </form>
                
                {/* Info Text */}
                <p className="mt-6 text-xs text-slate-500 text-center">
                  Diese Daten finden Sie in Ihrem Angebot oder in der 
                  E-Mail, die Sie von uns erhalten haben.
                </p>
              </CardContent>
            </Card>
          </div>
        ) : loadingOffer ? (
          /* ========== LOADING STATE ========== */
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="h-12 w-12 text-emerald-600 animate-spin mb-4" />
            <p className="text-slate-600">Angebot wird geladen...</p>
          </div>
        ) : offerData ? (
          /* ========== OFFER VIEW ========== */
          <div className="space-y-6">
            {/* Success Banner */}
            <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-emerald-800">Verifizierung erfolgreich</p>
                <p className="text-sm text-emerald-600">
                  Willkommen, {offerData.customer_name}!
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReset}
                className="ml-auto text-emerald-700 hover:text-emerald-900 hover:bg-emerald-100"
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                Anderes Angebot
              </Button>
            </div>
            
            {/* Offer Card */}
            <Card className="shadow-lg border-0">
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {offerData.company_logo ? (
                      <img 
                        src={offerData.company_logo} 
                        alt={offerData.company_name}
                        className="h-12 w-auto object-contain"
                      />
                    ) : (
                      <div className="p-2 bg-slate-100 rounded-lg">
                        <Building2 className="h-8 w-8 text-slate-400" />
                      </div>
                    )}
                    <div>
                      <CardTitle className="text-xl">{offerData.company_name}</CardTitle>
                      <CardDescription>Angebot {offerData.offer_number}</CardDescription>
                    </div>
                  </div>
                  {getStatusBadge(offerData.status)}
                </div>
              </CardHeader>
              
              <CardContent className="space-y-6">
                {/* Offer Details */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-sm text-slate-500">Angebotsnummer</p>
                    <p className="font-medium text-slate-900">{offerData.offer_number}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Datum</p>
                    <p className="font-medium text-slate-900">
                      {offerData.offer_date ? new Date(offerData.offer_date).toLocaleDateString("de-DE") : "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Kunde</p>
                    <p className="font-medium text-slate-900">{offerData.customer_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Gesamtsumme (brutto)</p>
                    <p className="font-semibold text-emerald-600 text-lg">{offerData.total_price}</p>
                  </div>
                </div>
                
                {/* PDF Viewer */}
                {showPdfViewer ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-slate-900">Angebot anzeigen</h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowPdfViewer(false)}
                      >
                        Ausblenden
                      </Button>
                    </div>
                    <div className="border rounded-lg overflow-hidden bg-slate-100" style={{ height: "600px" }}>
                      <iframe
                        src={`${API_URL}${offerData.pdf_url}`}
                        className="w-full h-full"
                        title="Angebot PDF"
                      />
                    </div>
                  </div>
                ) : (
                  <Button
                    data-testid="portal-view-pdf-btn"
                    variant="outline"
                    className="w-full h-12"
                    onClick={() => setShowPdfViewer(true)}
                  >
                    <Eye className="h-5 w-5 mr-2" />
                    Angebot als PDF anzeigen
                  </Button>
                )}
                
                {/* Signing Section */}
                {offerData.can_sign ? (
                  <div className="pt-4 border-t">
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                      <h3 className="font-medium text-amber-800 flex items-center gap-2">
                        <FileSignature className="h-5 w-5" />
                        Bereit zur Unterschrift
                      </h3>
                      <p className="text-sm text-amber-700 mt-1">
                        Prüfen Sie das Angebot und klicken Sie auf "Jetzt signieren", 
                        um den Signaturprozess zu starten. Sie erhalten dann einen 
                        Bestätigungscode per E-Mail.
                      </p>
                    </div>
                    
                    <Button
                      data-testid="portal-sign-btn"
                      onClick={handleRequestSignature}
                      disabled={requestingSignature}
                      className="w-full h-14 text-lg bg-emerald-600 hover:bg-emerald-700"
                    >
                      {requestingSignature ? (
                        <>
                          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                          Bestätigungscode wird gesendet...
                        </>
                      ) : (
                        <>
                          <Pen className="h-5 w-5 mr-2" />
                          Jetzt signieren
                          <ArrowRight className="h-5 w-5 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="pt-4 border-t">
                    <div className="p-4 bg-slate-100 border border-slate-200 rounded-lg">
                      <h3 className="font-medium text-slate-700 flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-slate-500" />
                        Signatur nicht möglich
                      </h3>
                      <p className="text-sm text-slate-600 mt-1">
                        Dieses Angebot wurde bereits bearbeitet oder kann nicht mehr 
                        unterschrieben werden. Bei Fragen kontaktieren Sie uns bitte.
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Error Message */}
                {error && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Session Info */}
            <p className="text-xs text-slate-400 text-center">
              Ihre Sitzung ist 30 Minuten gültig. Bei Inaktivität werden Sie 
              automatisch abgemeldet.
            </p>
          </div>
        ) : null}
      </div>
      
      {/* Footer */}
      <div className="mt-auto py-6 border-t bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm text-slate-500">
          <p>Sichere Verbindung • Ihre Daten werden verschlüsselt übertragen</p>
        </div>
      </div>
    </div>
  );
}
